/* eslint-disable max-len */

import {Injectable} from '@angular/core';
import {combineLatest, Observable, of, BehaviorSubject} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {BlueprintsService} from 'src/app/services/api/blueprints.service';
import {Solution} from '../../models/service/solution';
import {SolutionDetails} from '../../models/service/solution-details';
import {FabricService} from 'src/app/services/fabric/fabric.service';
import {LineOfBusiness} from 'src/app/models/line-of-business';
import {SolutionVersion} from 'src/app/models/service/solution-version';

const SERVICE_NAME = 'mgmt-solution-resources';

@Injectable({
  providedIn: 'root'
})
export class SolutionsService {

  mockedTutorialsData = [
    {
      title: 'Video One',
    },
    {
      title: 'Video Two',
    },
    {
      title: 'Video Three',
    },
    {
      title: 'Video Four',
    },
    {
      title: 'Video Five',
    },
    {
      title: 'Video Six',
    },
  ];

  mockedDocumentationData = [
    {
      title: 'Quick help on Configuration',
      description: 'Retail Banking - Lending helps Client banks of any size and business segment (Retail, Corporate, Business, Wealth and Universal).',
      link: 'https://www.temenos.com'
    },
    {
      title: 'How to guide',
      description: 'Retail Banking - Lending helps Client banks of any size and business segment (Retail, Corporate, Business, Wealth and Universal).',
      link: 'https://www.temenos.com'
    },
    {
      title: 'Installation of Packages',
      description: 'Retail Banking - Lending helps Client banks of any size and business segment (Retail, Corporate, Business, Wealth and Universal).',
      link: 'https://www.temenos.com'
    }
  ];

  constructor(private blueprintsService: BlueprintsService, private fabricService: FabricService) {
  }

  getSolutions(solutionObj?): Observable<Solution[]> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_solutions_mgmt_solutions', solutionObj);
  }

  getSolutionById(id: string): Observable<Solution> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_solutions_mgmt_solutions_id', {id});
  }

  getMySolutions(): Observable<Solution[]> {
    return combineLatest([this.getSolutions(), this.blueprintsService.getBlueprints()])
      .pipe(map(([solutions, blueprints]) => {
        const solutionsWithBlueprints = blueprints
          .reduce((acc, blueprint) => {
            acc.add(blueprint.solutionId);
            return acc;
          }, new Set());

        return solutions.filter((solution) => solutionsWithBlueprints.has(solution.id));
      }));
  }

  getSolutionDetailsById(id: string): Observable<SolutionDetails> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_solutions_mgmt_solutions_id', {id})
      .pipe(tap((solutionDetails) => {
        if (solutionDetails) {
          solutionDetails.tutorials = this.mockedTutorialsData;
          solutionDetails.documentation = this.mockedDocumentationData;
          return of(solutionDetails);
        }
      }));
  }

  getLineOfBusinesses(): Observable<LineOfBusiness[]> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_line-of-business');
  }

  getSolutionVersions(solutionId: string): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_solutions_mgmt_solutions_solutionid_versions', {solutionId});
  }

  getSolutionVersion(solutionId: string, solutionVersionId: string): Observable<SolutionVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_solutions_mgmt_solutions_solutionid_versions_id', {
      solutionId,
      id: solutionVersionId
    });
  }

  postSolution(solutionForm): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_solutions_mgmt_solutions', solutionForm);
  }

  putSolution(solutionForm): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_solutions_mgmt_solutions_id', solutionForm);
  }

  deleteSolution(id): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_solutions_mgmt_solutions_id', {id});
  }

  postBuildVersion(versionForm): Observable<SolutionVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_solutions_mgmt_solutions_solutionid_versions', versionForm);
  }

  putBuildVersion(versionForm): Observable<SolutionVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'put_solutions_mgmt_solutions_solutionid_versions_id', versionForm);
  }

  deleteBuildVersion(solutionId, id): Observable<SolutionVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_solutions_mgmt_solutions_solutionid_versions_id', {solutionId, id});
  }

  getSolutionVersionManifestById(solutionId: string, solutionVersionId?: string): Observable<SolutionVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_solutions_mgmt_solutions_solutionid_versions_solutionversionid_manifest', {solutionId, solutionVersionId });
  }

  postSolutionPackageReferences(obj): Observable<any> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'post_mgmt_solutions_solutionid_package-references', obj);
  }

  getReferencePackages(solutionId: string, tenantId?: string, size?: number): Observable<SolutionVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_solutions_solutionid_package-references', {solutionId, tenantId, size});
  }

  deleteReferencePackages(solutionId: string, packageReferenceId?: string): Observable<SolutionVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'delete_mgmt_solutions_solutionid_package-references_packagereferenceid', {solutionId, packageReferenceId});
  }

  getReferencePackagesById(solutionId: string, packageReferenceId?: string): Observable<SolutionVersion> {
    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'get_mgmt_solutions_solutionid_package-references_packagereferenceid', {solutionId, packageReferenceId});
  }
  
}
